"use client";

import { c as _c } from "react/compiler-runtime";
import type { WalletInterface } from "@bsv/sdk";
import { Wallet } from "lucide-react";
import Link from "next/link";
import { type ReactNode, useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { useOneSatWallet } from "@/lib/hooks/use-onesat-wallet";
import { WALLET_SIGNIN_REQUEST_PATH } from "@/lib/wallet-signin-contract";
type WalletSigninState = "idle" | "connecting" | "resolving" | "unlinked" | "error";
interface WalletSigninBody {
  bapId?: string;
  code?: string;
}
async function authenticateWallet(wallet: WalletInterface): Promise<string> {
  const {
    AuthFetch
  } = await import("@bsv/sdk/auth");
  const response = await new AuthFetch(wallet).fetch(new URL(WALLET_SIGNIN_REQUEST_PATH, window.location.origin).toString(), {
    body: "{}",
    headers: {
      "content-type": "application/json"
    },
    method: "POST"
  });
  let body: WalletSigninBody = {};
  try {
    body = (await response.json()) as WalletSigninBody;
  } catch {
    // The response status still maps to safe UI copy below.
  }
  if (response.ok && body.bapId) {
    return body.bapId;
  }
  if (response.status === 404 && body.code === "WALLET_NOT_LINKED") {
    throw new WalletNotLinkedError();
  }
  throw new Error("wallet_signin_failed");
}
class WalletNotLinkedError extends Error {
  constructor() {
    super("Wallet is not linked");
    this.name = "WalletNotLinkedError";
  }
}
function walletButtonLabel(state: WalletSigninState): string {
  if (state === "connecting") {
    return "Connecting...";
  }
  if (state === "resolving") {
    return "Finding your Sigma account...";
  }
  return "Continue with wallet";
}
function focusExistingSigninOptions(): void {
  // The alternatives live on the Restore tab; switch to it, then focus.
  const trigger = document.getElementById("login-restore-tab");
  if (trigger instanceof HTMLButtonElement) {
    trigger.click();
  }
  requestAnimationFrame(() => {
    const existing = document.getElementById("existing-sigma-sign-in-options");
    existing?.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
    const firstControl = existing?.querySelector<HTMLElement>("button, input, a");
    firstControl?.focus({
      preventScroll: true
    });
  });
}

/**
 * BRC-100 sign-in method for /login. Connecting only reads the wallet identity;
 * the subsequent AuthFetch request supplies fresh BRC-104 proof before Sigma
 * resolves an existing binding or creates any Better Auth session.
 */
export function LoginWalletDetect({
  disabled,
  onSignedIn,
  signupHref,
  onError
}: {
  disabled: boolean;
  onSignedIn: (bapId: string) => Promise<void>;
  signupHref: string;
  onError: (message: ReactNode) => void;
}) {
  const {
    connectAuto
  } = useOneSatWallet();
  const [state, setState] = useState<WalletSigninState>("idle");
  const handleContinue = useCallback(async () => {
    onError(null);
    setState("connecting");
    let walletConnected = false;
    try {
      const result = await connectAuto();
      if (!result) {
        throw new Error("wallet_unavailable");
      }
      walletConnected = true;
      setState("resolving");
      const bapId = await authenticateWallet(result.wallet);
      await onSignedIn(bapId);
    } catch (error) {
      const unlinked = error instanceof WalletNotLinkedError;
      setState(unlinked ? "unlinked" : "error");
      const failureMessage = walletConnected ? "Your wallet connected, but Sigma could not complete sign-in. Try again, or use another sign-in method." : "No BRC-100 wallet answered. Open your wallet and try again, or use another sign-in method.";
      onError(unlinked ? <UnlinkedWalletNotice signupHref={signupHref} /> : failureMessage);
    }
  }, [connectAuto, onSignedIn, onError, signupHref]);
  const pending = state === "connecting" || state === "resolving";
  return <div className="space-y-2" data-testid="login-wallet-detect">
			<Button aria-describedby="login-wallet-detect-help" className="w-full" disabled={disabled || pending} onClick={handleContinue} size="lg" type="button">
				<Wallet className="mr-2 h-4 w-4" />
				{walletButtonLabel(state)}
			</Button>
			<p className="sr-only" id="login-wallet-detect-help">
				{"Sign in with a linked wallet"}
				{". "}
				{"Use a BRC-100 wallet linked to your Sigma account."}
			</p>
		</div>;
}
function UnlinkedWalletNotice(t0) {
  const $ = _c(4);
  const {
    signupHref
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="space-y-1"><p className="font-medium text-sm">This wallet isn't linked to a Sigma account</p><p className="text-muted-foreground text-xs">Sign in first and link it from Credentials, or create a Sigma account.</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Button onClick={focusExistingSigninOptions} type="button" variant="outline">Sign in to existing account</Button>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== signupHref) {
    t3 = <div className="space-y-3 text-center">{t1}<div className="grid gap-2 sm:grid-cols-2">{t2}<Button asChild={true}><Link href={signupHref}>Create a Sigma account</Link></Button></div></div>;
    $[2] = signupHref;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}